import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '../../theme';

export interface AdminEngagementOverviewPoint {
  label: string;
  totalUsers: number;
  activeUsers: number;
}

export interface AdminEngagementOverviewChartProps {
  /** Série temporal (ex.: meses) */
  points: AdminEngagementOverviewPoint[];
  legendTotalLabel: string;
  legendActiveLabel: string;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  loading?: boolean;
  error?: boolean;
}

/** Eixo Y com passo 45 (mock: 0, 45, …, 180); expande se os dados forem maiores. */
function buildEngagementYAxis(
  rows: { totalUsers: number; activeUsers: number }[]
): { yMax: number; ticks: number[] } {
  const maxY = Math.max(
    0,
    ...rows.flatMap(p => [p.totalUsers, p.activeUsers])
  );
  const stepBase = 45;
  let top = Math.max(180, Math.ceil(maxY / stepBase) * stepBase);
  const tickCount = Math.floor(top / stepBase) + 1;

  if (tickCount > 10) {
    const step = Math.max(stepBase, Math.ceil(top / 6 / stepBase) * stepBase);
    const ticks: number[] = [];
    for (let v = 0; v <= top; v += step) {
      ticks.push(v);
    }
    if (ticks[ticks.length - 1] < top) {
      ticks.push(top);
    }
    return { yMax: top, ticks };
  }

  const ticks: number[] = [];
  for (let v = 0; v <= top; v += stepBase) {
    ticks.push(v);
  }
  return { yMax: top, ticks };
}

export function AdminEngagementOverviewChart({
  points,
  legendTotalLabel,
  legendActiveLabel,
  loadingLabel,
  emptyLabel,
  errorLabel,
  loading,
  error,
}: AdminEngagementOverviewChartProps) {
  const theme = useTheme();
  const c = theme.palette.adminDashboard.charts;
  const h = c.engagementChartHeightPx;

  const data = useMemo(
    () =>
      points.map(p => ({
        name: p.label,
        totalUsers: p.totalUsers,
        activeUsers: p.activeUsers,
      })),
    [points]
  );

  const { yMax, ticks } = useMemo(
    () => buildEngagementYAxis(data),
    [data]
  );

  const bodyStyle: CSSProperties = {
    width: '100%',
    height: h,
    minHeight: h,
  };

  if (loading) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {loadingLabel}
      </p>
    );
  }

  if (error) {
    return (
      <p
        style={{
          margin: 0,
          color: theme.palette.error.main,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {errorLabel}
      </p>
    );
  }

  if (!points.length) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div style={bodyStyle}>
      <ResponsiveContainer
        width="100%"
        height="100%"
      >
        <LineChart
          data={data}
          margin={{ top: 8, right: 8, left: 4, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={c.gridStroke}
            vertical
            horizontal
          />
          <XAxis
            dataKey="name"
            tick={{ fill: c.axisTick, fontSize: 12 }}
            axisLine={{ stroke: c.gridStroke }}
            tickLine={false}
          />
          <YAxis
            domain={[0, yMax]}
            ticks={ticks}
            tickFormatter={v => `${v}`}
            tick={{ fill: c.axisTick, fontSize: 12 }}
            axisLine={{ stroke: c.gridStroke }}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: theme.shape.borderRadius.sm2,
              border: `${theme.shape.borderWidth.thin} solid ${c.cardBorder}`,
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: theme.typography.fontSize.xs }}
          />
          <Line
            type="monotone"
            dataKey="totalUsers"
            name={legendTotalLabel}
            stroke={c.lineTotalUsers}
            strokeWidth={2}
            dot={{
              r: 4,
              fill: c.lineDotFill,
              stroke: c.lineTotalUsers,
              strokeWidth: 2,
            }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="activeUsers"
            name={legendActiveLabel}
            stroke={c.lineActiveUsers}
            strokeWidth={2}
            dot={{
              r: 4,
              fill: c.lineDotFill,
              stroke: c.lineActiveUsers,
              strokeWidth: 2,
            }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
